import React, { useEffect, useMemo, useState } from 'react';
import { Search, Building2, Users, GraduationCap, ArrowUpRight, IndianRupee, Plus, X, Copy, Check, MessageCircle, Sparkles } from 'lucide-react';
import api from '../../utils/api';
import './platform.css';

const PLAN_OPTIONS = [
  { id: 'free',       label: 'Free',       hint: '14-day trial · up to 50 students' },
  { id: 'basic',      label: 'Basic',      hint: '₹0/mo · up to 250 students' },
  { id: 'premium',    label: 'Premium',    hint: '₹3,000/mo · up to 1,000 students' },
  { id: 'enterprise', label: 'Enterprise', hint: 'Custom price · up to 10,000 students' }
];

const STATUS_TONE = {
  active:    'ok',
  trialing:  'warn',
  paused:    'muted',
  cancelled: 'danger',
};

const PLAN_META = {
  free:       { label: 'Free',       color: '#8a7560' },
  basic:      { label: 'Basic',      color: '#3b6b2e' },
  premium:    { label: 'Premium',    color: '#b66a12' },
  enterprise: { label: 'Enterprise', color: '#4b2a6b' },
};

const PlatformSchoolsPage = () => {
  const [schools, setSchools] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [plan, setPlan] = useState('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [seedState, setSeedState] = useState({});

  // Create-school modal state
  const [showCreate, setShowCreate] = useState(false);
  const [justCreated, setJustCreated] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const { data } = await api.get('/platform/schools', {
          params: { page: pagination.page, limit: 20, search: search || undefined, status: status || undefined, plan: plan || undefined }
        });
        if (!cancelled) {
          setSchools(data.schools || []);
          setPagination(data.pagination || { page: 1, pages: 1, total: 0 });
        }
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.message || 'Could not load schools');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [pagination.page, search, status, plan, reloadKey]);

  const aggregate = useMemo(() => {
    const totalStudents = schools.reduce((s, x) => s + (x.usage?.students || 0), 0);
    const totalTeachers = schools.reduce((s, x) => s + (x.usage?.teachers || 0), 0);
    // MRR on this page = sum of non-null prices on active schools only.
    const pageMrr = schools.reduce((sum, x) =>
      sum + (x.status === 'active' && x.monthlyPrice != null ? Number(x.monthlyPrice) : 0), 0);
    return { totalStudents, totalTeachers, pageMrr };
  }, [schools]);

  const formatPrice = (n) => n == null ? null : `₹${Number(n).toLocaleString('en-IN')}`;

  // Toggle the per-school WhatsApp opt-in. Optimistic update so the icon
  // flips immediately; rolls back if the PATCH fails.
  const toggleWhatsapp = async (school) => {
    const next = !school.whatsappEnabled;
    setSchools((prev) => prev.map((s) => s.id === school.id ? { ...s, whatsappEnabled: next } : s));
    try {
      await api.patch(`/platform/schools/${school.id}`, { whatsappEnabled: next });
    } catch (e) {
      setSchools((prev) => prev.map((s) => s.id === school.id ? { ...s, whatsappEnabled: !next } : s));
      setErr(e?.response?.data?.message || 'Could not update WhatsApp setting');
      setTimeout(() => setErr(null), 3500);
    }
  };

  // Populate an empty tenant with sample students/teachers/attendance so
  // the analytics dashboard has something to render in demos. Backend
  // refuses if the tenant already has students.
  const seedDemo = async (school) => {
    if (!window.confirm(`Seed sample data into ${school.name}?\n\nThis adds ~60 students, 8 teachers, and 5 days of attendance. Only works if the tenant is empty.`)) return;
    setSeedState((m) => ({ ...m, [school.id]: { loading: true } }));
    try {
      const { data } = await api.post(`/platform/schools/${school.id}/seed-demo`);
      setSeedState((m) => ({ ...m, [school.id]: { ok: true, seeded: data.seeded } }));
      setReloadKey((k) => k + 1);
      setTimeout(() => setSeedState((m) => { const n = { ...m }; delete n[school.id]; return n; }), 5000);
    } catch (e) {
      setSeedState((m) => ({ ...m, [school.id]: { error: e?.response?.data?.message || 'Could not seed data' } }));
      setTimeout(() => setSeedState((m) => { const n = { ...m }; delete n[school.id]; return n; }), 6000);
    }
  };

  // Compose the school's branded login URL. On production we use the real
  // wildcard subdomain; on localhost/dev we fall back to the ?tenant= override
  // so the owner can still preview a school without editing /etc/hosts.
  const tenantUrl = (code) => {
    const host = window.location.hostname;
    const isDev = host === 'localhost' || host === '127.0.0.1';
    if (isDev) {
      return `${window.location.protocol}//${host}:${window.location.port}/login?tenant=${encodeURIComponent(code)}`;
    }
    // Strip the leading "edu." or "www." to get the apex the tenant lives under.
    const apex = host.replace(/^(www|edu)\./, '');
    return `${window.location.protocol}//${code.toLowerCase()}.edu.${apex}`;
  };

  return (
    <div className="pf-page">
      <header className="pf-page-head">
        <div>
          <div className="pf-eyebrow">Platform</div>
          <h1 className="pf-title">Schools</h1>
          <p className="pf-sub">Every tenant on the platform, with live usage vs. their plan limits.</p>
        </div>
        <button className="pf-cta" onClick={() => setShowCreate(true)}>
          <Plus size={15} /> New school
        </button>
      </header>

      {/* Summary tiles — page-local. */}
      <div className="pf-tiles pf-tiles-tight">
        <SummaryTile icon={Building2} label="Schools on this page" value={pagination.total} />
        <SummaryTile icon={IndianRupee} label="MRR on this page" value={aggregate.pageMrr} money />
        <SummaryTile icon={GraduationCap} label="Students combined" value={aggregate.totalStudents} />
        <SummaryTile icon={Users} label="Teachers combined" value={aggregate.totalTeachers} />
      </div>

      {/* Filter bar */}
      <div className="pf-filters">
        <div className="pf-search">
          <Search size={15} />
          <input
            type="text"
            placeholder="Search by name, code, or email"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}
          />
        </div>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}>
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="trialing">Trialing</option>
          <option value="paused">Paused</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select value={plan} onChange={(e) => { setPlan(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}>
          <option value="">All plans</option>
          <option value="free">Free</option>
          <option value="basic">Basic</option>
          <option value="premium">Premium</option>
          <option value="enterprise">Enterprise</option>
        </select>
      </div>

      {err && <div className="pf-error">{err}</div>}

      <div className="pf-card pf-table-card">
        {loading ? (
          <div className="pf-loading"><div className="pf-spinner" /> Loading schools…</div>
        ) : schools.length === 0 ? (
          <div className="pf-empty pf-empty-big">
            <Building2 size={28} />
            <div>No schools match those filters.</div>
          </div>
        ) : (
          <table className="pf-table">
            <thead>
              <tr>
                <th>School</th>
                <th>Code</th>
                <th>Plan</th>
                <th>Price</th>
                <th>Status</th>
                <th>Students</th>
                <th>Teachers</th>
                <th>Signed up</th>
                <th title="WhatsApp broadcast enabled for this school?">WA</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {schools.map((s) => {
                const planMeta = PLAN_META[s.subscriptionPlan] || { label: s.subscriptionPlan, color: '#8a7560' };
                const statusTone = STATUS_TONE[s.status] || 'muted';
                const stuPct = s.maxStudents > 0 ? Math.min(100, Math.round(((s.usage?.students || 0) / s.maxStudents) * 100)) : 0;
                const teaPct = s.maxTeachers > 0 ? Math.min(100, Math.round(((s.usage?.teachers || 0) / s.maxTeachers) * 100)) : 0;

                return (
                  <tr key={s.id}>
                    <td>
                      <div className="pf-sch-name">{s.name}</div>
                      <div className="pf-sch-meta">
                        {s.addressCity ? `${s.addressCity}, ${s.addressState || ''}` : (s.email || '—')}
                      </div>
                    </td>
                    <td><span className="pf-code">{s.code}</span></td>
                    <td>
                      <span className="pf-plan-pill" style={{ background: planMeta.color + '18', color: planMeta.color, borderColor: planMeta.color + '30' }}>
                        {planMeta.label}
                      </span>
                    </td>
                    <td>
                      {s.monthlyPrice == null ? (
                        <span className="pf-price-custom">Custom</span>
                      ) : s.monthlyPrice === 0 ? (
                        <span className="pf-dim">Free</span>
                      ) : (
                        <span className="pf-price">{formatPrice(s.monthlyPrice)}<span className="pf-price-unit"> /mo</span></span>
                      )}
                    </td>
                    <td>
                      <span className={`pf-status-pill tone-${statusTone}`}>{s.status}</span>
                    </td>
                    <td>
                      <Usage used={s.usage?.students || 0} max={s.maxStudents} pct={stuPct} />
                    </td>
                    <td>
                      <Usage used={s.usage?.teachers || 0} max={s.maxTeachers} pct={teaPct} />
                    </td>
                    <td className="pf-dim">{s.createdAt ? new Date(s.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</td>
                    <td>
                      <button
                        type="button"
                        onClick={() => toggleWhatsapp(s)}
                        title={s.whatsappEnabled ? 'WhatsApp ON — click to disable' : 'WhatsApp OFF — click to enable'}
                        className="pf-row-btn"
                        style={s.whatsappEnabled
                          ? { background: '#ecfdf5', borderColor: '#a7f3d0', color: '#16a34a' }
                          : { background: 'transparent', color: '#9ca3af' }}
                      >
                        <MessageCircle size={14} />
                      </button>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {(s.usage?.students || 0) === 0 && (
                          <button
                            type="button"
                            onClick={() => seedDemo(s)}
                            disabled={seedState[s.id]?.loading}
                            className="pf-row-btn"
                            title={
                              seedState[s.id]?.ok
                                ? `Seeded ${seedState[s.id].seeded.students} students + ${seedState[s.id].seeded.teachers} teachers`
                                : seedState[s.id]?.error
                                  ? seedState[s.id].error
                                  : 'Populate this empty tenant with demo students, teachers, and attendance'
                            }
                            style={
                              seedState[s.id]?.ok
                                ? { background: '#ecfdf5', borderColor: '#a7f3d0', color: '#16a34a' }
                                : seedState[s.id]?.error
                                  ? { background: '#fdecea', borderColor: '#edbcb6', color: '#7a1c15' }
                                  : { background: '#fef3d8', borderColor: '#e6d5ad', color: '#8a540a' }
                            }
                          >
                            {seedState[s.id]?.loading
                              ? <span className="pf-spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />
                              : seedState[s.id]?.ok
                                ? <Check size={14} />
                                : <Sparkles size={14} />}
                          </button>
                        )}
                        <a
                          className="pf-row-btn"
                          href={tenantUrl(s.code)}
                          target="_blank"
                          rel="noreferrer"
                          title={`Open ${s.name} login`}
                        >
                          <ArrowUpRight size={14} />
                        </a>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {pagination.pages > 1 && (
          <div className="pf-pagination">
            <span>Page {pagination.page} of {pagination.pages} · {pagination.total} total</span>
            <div>
              <button disabled={pagination.page === 1} onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}>Prev</button>
              <button disabled={pagination.page === pagination.pages} onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}>Next</button>
            </div>
          </div>
        )}
      </div>

      {showCreate && (
        <CreateSchoolModal
          onClose={() => setShowCreate(false)}
          onCreated={(data) => {
            setShowCreate(false);
            setJustCreated(data);
            setReloadKey((k) => k + 1);
          }}
        />
      )}

      {justCreated && (
        <CreatedSuccessPanel
          data={justCreated}
          onClose={() => setJustCreated(null)}
        />
      )}
    </div>
  );
};

const SummaryTile = ({ icon: Icon, label, value, money }) => (
  <div className="pf-tile pf-tile-sm">
    <span className="pf-tile-ic" style={{ color: '#b66a12', background: '#b66a1214' }}>
      <Icon size={16} />
    </span>
    <div>
      <div className="pf-tile-value">
        {money ? `₹${Number(value).toLocaleString('en-IN')}` : Number(value).toLocaleString('en-IN')}
      </div>
      <div className="pf-tile-label">{label}</div>
    </div>
  </div>
);

const Usage = ({ used, max, pct }) => (
  <div className="pf-usage">
    <div className="pf-usage-top">
      <span className="pf-usage-num">{used.toLocaleString('en-IN')}</span>
      <span className="pf-usage-max">/ {max ? max.toLocaleString('en-IN') : '∞'}</span>
    </div>
    <div className="pf-usage-bar">
      <div
        className="pf-usage-fill"
        style={{
          width: `${pct}%`,
          background: pct >= 90 ? '#b42318' : pct >= 70 ? '#b66a12' : '#3b6b2e'
        }}
      />
    </div>
  </div>
);

// =========================================================================
// Create school — owner-only modal
// =========================================================================
const CreateSchoolModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({
    name: '',
    code: '',
    contactEmail: '',
    phone: '',
    addressCity: '',
    addressState: '',
    plan: 'premium',
    adminFirstName: '',
    adminLastName: '',
    adminEmail: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const change = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({
      ...f,
      [name]: name === 'code' ? value.toUpperCase().replace(/[^A-Z0-9]/g, '') : value
    }));
    setErr(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    try {
      const { data } = await api.post('/platform/schools', form);
      onCreated(data);
    } catch (e2) {
      setErr(e2?.response?.data?.error?.message || e2?.response?.data?.message || 'Could not create school');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pf-overlay" onClick={onClose}>
      <div className="pf-modal" onClick={(e) => e.stopPropagation()}>
        <button className="pf-modal-close" onClick={onClose} aria-label="Close">
          <X size={18} />
        </button>
        <div className="pf-modal-head">
          <div className="pf-eyebrow">Platform</div>
          <h2 className="pf-modal-title">Onboard a new school</h2>
          <p className="pf-sub">Creates the tenant and its first admin login in one step.</p>
        </div>

        <form onSubmit={submit}>
          <div className="pf-modal-body">
            <section className="pf-form-section">
              <h4 className="pf-form-section-title">School</h4>
              <div className="pf-form-grid">
                <label className="pf-field pf-field-full">
                  <span>School name *</span>
                  <input name="name" value={form.name} onChange={change} required placeholder="e.g. Kakatiya High School" />
                </label>
                <label className="pf-field">
                  <span>Code * <em>(becomes the subdomain)</em></span>
                  <input name="code" value={form.code} onChange={change} required placeholder="KAKATIYA" maxLength={20} />
                  {form.code && (
                    <small className="pf-field-hint">
                      {form.code.toLowerCase()}.edu.rtconnect.in
                    </small>
                  )}
                </label>
                <label className="pf-field">
                  <span>Contact email *</span>
                  <input type="email" name="contactEmail" value={form.contactEmail} onChange={change} required placeholder="office@kakatiya.edu" />
                </label>
                <label className="pf-field">
                  <span>Phone</span>
                  <input name="phone" value={form.phone} onChange={change} placeholder="+91 …" />
                </label>
                <label className="pf-field">
                  <span>City</span>
                  <input name="addressCity" value={form.addressCity} onChange={change} placeholder="Warangal" />
                </label>
                <label className="pf-field">
                  <span>State</span>
                  <input name="addressState" value={form.addressState} onChange={change} placeholder="Telangana" />
                </label>
              </div>
            </section>

            <section className="pf-form-section">
              <h4 className="pf-form-section-title">Plan</h4>
              <div className="pf-plan-options">
                {PLAN_OPTIONS.map((p) => (
                  <label key={p.id} className={`pf-plan-option ${form.plan === p.id ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="plan"
                      value={p.id}
                      checked={form.plan === p.id}
                      onChange={change}
                    />
                    <div>
                      <div className="pf-plan-option-label">{p.label}</div>
                      <div className="pf-plan-option-hint">{p.hint}</div>
                    </div>
                  </label>
                ))}
              </div>
            </section>

            <section className="pf-form-section">
              <h4 className="pf-form-section-title">First admin</h4>
              <p className="pf-form-note">We generate a one-time password you can hand to them.</p>
              <div className="pf-form-grid">
                <label className="pf-field">
                  <span>First name *</span>
                  <input name="adminFirstName" value={form.adminFirstName} onChange={change} required />
                </label>
                <label className="pf-field">
                  <span>Last name *</span>
                  <input name="adminLastName" value={form.adminLastName} onChange={change} required />
                </label>
                <label className="pf-field pf-field-full">
                  <span>Admin email *</span>
                  <input type="email" name="adminEmail" value={form.adminEmail} onChange={change} required placeholder="admin@kakatiya.edu" />
                </label>
              </div>
            </section>

            {err && <div className="pf-error">{err}</div>}
          </div>

          <div className="pf-modal-footer">
            <button type="button" className="pf-btn-ghost" onClick={onClose} disabled={submitting}>Cancel</button>
            <button type="submit" className="pf-cta" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create school'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// =========================================================================
// Post-creation success panel — surfaces the subdomain URL and temp password
// with one-tap copy buttons so the owner can share it right away.
// =========================================================================
const CreatedSuccessPanel = ({ data, onClose }) => {
  const [copied, setCopied] = useState(null);
  const copy = (key, value) => {
    navigator.clipboard?.writeText(value);
    setCopied(key);
    setTimeout(() => setCopied((c) => (c === key ? null : c)), 1500);
  };

  const rows = [
    { key: 'url', label: 'School URL', value: data.subdomain },
    { key: 'email', label: 'Admin email', value: data.admin.email },
    { key: 'password', label: 'Temporary password', value: data.admin.temporaryPassword, mono: true }
  ];

  return (
    <div className="pf-overlay" onClick={onClose}>
      <div className="pf-modal pf-modal-success" onClick={(e) => e.stopPropagation()}>
        <button className="pf-modal-close" onClick={onClose} aria-label="Close">
          <X size={18} />
        </button>
        <div className="pf-modal-head">
          <div className="pf-eyebrow" style={{ color: '#2f5423' }}>Success</div>
          <h2 className="pf-modal-title">{data.school.name} is live</h2>
          <p className="pf-sub">Hand these to the school's admin — they can log in right now.</p>
        </div>

        <div className="pf-modal-body">
          <div className="pf-credbox">
            {rows.map((r) => (
              <div key={r.key} className="pf-cred">
                <div className="pf-cred-label">{r.label}</div>
                <div className="pf-cred-value-row">
                  <code className={`pf-cred-value ${r.mono ? 'mono' : ''}`}>{r.value}</code>
                  <button type="button" className="pf-row-btn" onClick={() => copy(r.key, r.value)} title="Copy">
                    {copied === r.key ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                </div>
              </div>
            ))}
          </div>
          <p className="pf-form-note">
            The admin should change their password after first login. The temporary password
            won't be shown again — copy it now.
          </p>
        </div>

        <div className="pf-modal-footer">
          <button className="pf-cta" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
};

export default PlatformSchoolsPage;
